// ─── RegistryUrlValidationService™ ───────────────────────────────────────────
// Sprint 113: Server-Side URL Validation — Registry Layer
//
// PURPOSE:
//   MissionSourceRegistry.ts contains hardcoded websiteUrl / dashboardUrl fields.
//   These URLs can go dead at any time (domain changes, acquisitions, shutdowns).
//   This service validates every registry URL against the server's live HTTP probe
//   and caches the result in localStorage for 24 hours.
//
// HOW IT WORKS:
//   1. On app mount, refreshRegistryUrls() is called once.
//   2. If the cache is fresh (< 24 hours), it returns immediately.
//   3. If stale or missing, it calls POST /api/url/validate-batch with all registry URLs.
//   4. The server does real HTTP HEAD probes and returns per-url status.
//   5. Results are written to localStorage with a timestamp.
//   6. resolveVisitUrl() in OpportunityQueueCard reads this cache before opening a URL.
//
// DEAD URL BEHAVIOUR:
//   • getRegistryUrlStatus(sourceId) returns 'DEAD' → Visit Website disabled
//   • UI shows "Official website temporarily unavailable."
//   • Dead URL is never opened in a new tab
//
// CACHE KEY: nomad:registry:url:v1
// TTL:       24 hours (stale-while-revalidate pattern: show old data, refresh async)

import { getAllSources } from './MissionSourceRegistry';

// ─── Types ────────────────────────────────────────────────────────────────────

export type RegistryUrlStatus = 'OK' | 'DEAD' | 'PENDING';

export interface RegistryUrlEntry {
  status:    RegistryUrlStatus;
  finalUrl:  string;            // canonical URL after redirects
  checkedAt: string;            // ISO 8601
}

type RegistryCache = Record<string, RegistryUrlEntry>; // keyed by sourceId

// ─── Constants ────────────────────────────────────────────────────────────────

const CACHE_KEY  = 'nomad:registry:url:v1';
const TTL_MS     = 24 * 60 * 60 * 1000; // 24 hours

// ─── Cache I/O ────────────────────────────────────────────────────────────────

function loadCache(): RegistryCache {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as RegistryCache) : {};
  } catch {
    return {};
  }
}

function saveCache(cache: RegistryCache): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch { /* localStorage full or unavailable */ }
}

function isCacheFresh(cache: RegistryCache): boolean {
  const entries = Object.values(cache);
  if (entries.length === 0) return false;
  // Consider fresh if the most recent check is < TTL_MS old
  const most_recent = Math.max(
    ...entries.map(e => new Date(e.checkedAt).getTime())
  );
  return Date.now() - most_recent < TTL_MS;
}

// ─── In-progress guard ────────────────────────────────────────────────────────
// Prevent concurrent refreshes (e.g. two components mounting simultaneously)

let _refreshInFlight = false;

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Returns the validated status for a registry source's websiteUrl.
 *
 * Returns:
 *   'OK'      — URL is alive and reachable in a real browser
 *   'DEAD'    — URL is confirmed unreachable (Visit Website should be disabled)
 *   'PENDING' — Not yet validated (treat as OK — optimistic until proven dead)
 */
export function getRegistryUrlStatus(sourceId: string): RegistryUrlStatus {
  try {
    const cache = loadCache();
    return cache[sourceId]?.status ?? 'PENDING';
  } catch {
    return 'PENDING';
  }
}

/**
 * Returns the canonical (post-redirect) URL for a registry source, or null if dead.
 * Use this instead of source.websiteUrl for the Visit Website button.
 */
export function getRegistryCanonicalUrl(sourceId: string, fallbackUrl: string): string | null {
  try {
    const cache = loadCache();
    const entry = cache[sourceId];
    if (!entry) return fallbackUrl; // PENDING — use the registry URL optimistically
    if (entry.status === 'DEAD') return null;
    return entry.finalUrl || fallbackUrl;
  } catch {
    return fallbackUrl;
  }
}

/**
 * Refresh the registry URL validation cache.
 *
 * Called once on app mount (App.tsx useEffect).
 * - If cache is fresh (< 24 hours), returns immediately.
 * - Otherwise, calls POST /api/url/validate-batch with all registry URLs.
 * - Concurrent calls are deduped (only one in-flight at a time).
 *
 * @param force — bypass TTL check and re-validate regardless of freshness
 */
export async function refreshRegistryUrls(force = false): Promise<void> {
  if (_refreshInFlight) return;

  const cache = loadCache();
  if (!force && isCacheFresh(cache)) return;

  _refreshInFlight = true;

  try {
    const sources = getAllSources();

    // Build one entry per source with its websiteUrl (the public landing page)
    const entries = sources
      .filter(s => s.websiteUrl && s.websiteUrl.startsWith('https://'))
      .map(s => ({ id: s.id, name: s.companyName, url: s.websiteUrl }));

    if (entries.length === 0) return;

    const res = await fetch('/api/url/validate-batch', {
      method:      'POST',
      credentials: 'include',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ entries }),
    });

    if (!res.ok) {
      console.warn('[RegistryUrlValidation] validate-batch failed:', res.status);
      return;
    }

    const data = await res.json() as {
      results: Array<{
        id:        string;
        ok:        boolean;
        finalUrl:  string;
        checkedAt: string;
      }>;
    };

    const updated: RegistryCache = { ...cache };
    for (const r of data.results) {
      updated[r.id] = {
        status:    r.ok ? 'OK' : 'DEAD',
        finalUrl:  r.finalUrl,
        checkedAt: r.checkedAt,
      };
    }

    saveCache(updated);

    const dead = data.results.filter(r => !r.ok);
    if (dead.length > 0) {
      console.warn(
        `[RegistryUrlValidation] ${dead.length} dead registry URL(s) detected:`,
        dead.map(r => `${r.id}: ${r.finalUrl}`)
      );
    }
  } catch (err) {
    console.warn('[RegistryUrlValidation] refresh failed (network error):', err);
  } finally {
    _refreshInFlight = false;
  }
}

/**
 * Force-clear the cache and immediately re-validate all registry URLs.
 * Used by the Developer Settings page for manual re-validation.
 */
export async function forceRevalidateRegistry(): Promise<void> {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch { /* ignore */ }
  await refreshRegistryUrls(true);
}

/**
 * Return a summary of cached validation results.
 * Used by /system/health and Developer Settings for diagnostics.
 */
export function getRegistryValidationSummary(): {
  total: number;
  ok: number;
  dead: number;
  pending: number;
  lastCheckedAt: string | null;
  deadSources: string[];
} {
  const cache = loadCache();
  const entries = Object.entries(cache);

  const sources = getAllSources();
  const total   = sources.length;
  const cached  = entries.length;
  const ok      = entries.filter(([, v]) => v.status === 'OK').length;
  const dead    = entries.filter(([, v]) => v.status === 'DEAD').length;
  const pending = total - cached;

  const timestamps = entries.map(([, v]) => new Date(v.checkedAt).getTime());
  const lastCheckedAt = timestamps.length > 0
    ? new Date(Math.max(...timestamps)).toISOString()
    : null;

  const deadSources = entries
    .filter(([, v]) => v.status === 'DEAD')
    .map(([id]) => id);

  return { total, ok, dead, pending, lastCheckedAt, deadSources };
}
